import { PERSON_SEX_TEXT } from '@rooming/web-core';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, DataList, FilterChips, Spinner, useToast } from '@rooming/ui';
import { useState } from 'react';
import { passengerApi, type RosterEntry } from '../api';

type Filter = 'all' | 'without' | 'with';

/**
 * The FIRMANTE's list of every passenger of the group and their room (prompt10 §3, §10): who still has no place, so they
 * can be contacted. Read-only. Kept fresh by the board's live updates (same query key family).
 *
 * Owner's decision of 2026-10-06 (DR-326): the document number is part of the list from the first moment — no button.
 * It is still an AUDITED read, made ONCE when the screen opens and kept in memory: the live refreshes of the list do not
 * read documents again. With a group of a hundred the list is long, so it filters by "sin plaza" / "con plaza".
 */
export function RosterPanel({ stayId }: { readonly stayId: string }) {
  const roster = useQuery({
    queryKey: ['distribution', stayId, 'roster'],
    queryFn: async () => (await passengerApi.roster(stayId)).data.items,
  });
  // A different key family on purpose: board changes must not trigger another audited read.
  const documents = useQuery({
    queryKey: ['roster-documents', stayId],
    queryFn: async () =>
      new Map((await passengerApi.roster(stayId, true)).data.items.map((p) => [p.memberId, p.document?.number ?? '—'])),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const [filter, setFilter] = useState<Filter>('all');
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const download = (): void => {
    setDownloading(true);
    passengerApi
      .downloadRoomingList(stayId)
      .then(() => {
        toast('success', 'Rooming list descargada.');
      })
      .catch(() => {
        toast('error', 'No pudimos descargar el rooming list. Probá de nuevo.');
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  if (roster.isPending) return <Spinner />;
  if (roster.data === undefined) return null;
  const without = roster.data.filter((p) => p.room === null);
  const withPlace = roster.data.filter((p) => p.room !== null);
  const rows = filter === 'without' ? without : filter === 'with' ? withPlace : roster.data;
  const filters: readonly [Filter, string][] = [
    ['all', `Todos (${String(roster.data.length)})`],
    ['without', `Sin plaza (${String(without.length)})`],
    ['with', `Con plaza (${String(withPlace.length)})`],
  ];
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Pasajeros del grupo</h2>
        <Button variant="secondary" loading={downloading} onClick={download} className="min-h-11">
          <span aria-hidden="true">⬇ </span>Descargar rooming list
        </Button>
      </div>
      <FilterChips label="Filtrar pasajeros" value={filter} onChange={setFilter} options={filters} />
      <p className="text-base">
        {without.length === 0
          ? 'Todos tienen plaza.'
          : `${String(without.length)} sin plaza: contactalos para que elijan.`}
      </p>
      <DataList<RosterEntry>
        caption="Pasajeros del grupo"
        rows={rows}
        rowKey={(p) => p.memberId}
        empty={
          roster.data.length === 0
            ? 'El grupo todavía no tiene pasajeros.'
            : filter === 'without'
              ? 'Nadie está sin plaza.'
              : 'Nadie tiene plaza todavía.'
        }
        columns={[
          {
            header: 'Pasajero',
            // DR-408: «Pendiente de activar» = todavía entra con su documento: a esta persona hay que pasarle el enlace.
            cell: (p) => (
              <span className="inline-flex flex-wrap items-center gap-2">
                {`${p.lastNames}, ${p.firstNames}`}
                {p.accountState === 'PENDING' && <Badge tone="warning">Pendiente de activar</Badge>}
                {p.accountState === 'ACTIVATED' && <Badge tone="success">Activo</Badge>}
              </span>
            ),
          },
          {
            header: 'Tipo',
            cell: (p) =>
              p.role === 'COMPANION' ? <Badge tone="info">Acompañante</Badge> : <Badge tone="neutral">Pasajero</Badge>,
          },
          { header: 'Sexo', cell: (p) => PERSON_SEX_TEXT[p.sex] ?? p.sex },
          {
            header: 'Documento',
            cell: (p) => (
              <span className="font-mono">
                {documents.data?.get(p.memberId) ?? (documents.isPending ? '…' : 'No disponible')}
              </span>
            ),
          },
          { header: 'Estado', cell: (p) => (p.room === null ? 'Sin plaza' : 'Con plaza') },
          { header: 'Habitación', cell: (p) => p.room ?? '—' },
          {
            header: 'Habitación del hotel',
            cell: (p) => (p.room === null ? '—' : (p.physicalRoom ?? 'Todavía no disponible')),
          },
        ]}
      />
    </section>
  );
}
